import { observer } from "mobx-react-lite"
import { Control, useController } from "react-hook-form"
import Createable from "react-select/creatable"
import classNames from "classnames"
import { colors } from "@theme/colors"
import ChevronDownFill from "~icons/eva/chevron-down-fill"
import ChevronUpFill from "~icons/eva/chevron-up-fill"
import CloseOutline from "~icons/eva/close-outline"
import { UseMutationResult } from "@tanstack/react-query"
import styles from "../DefaultInput/styles.module.scss"
import createableStyles from "./styles.module.scss"

export type SelectOption = { value: string; label: string }

export const FormSelectCreateableInput = observer(
  ({
    control,
    name,
    options,
    label,
    createOptionMutation,
    className,
    isDisabled = false,
    parentBackgroundColor,
    lightOutline,
  }: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    control: Control<any>
    name: string
    options: SelectOption[]
    label: string
    createOptionMutation: UseMutationResult<SelectOption>
    className?: string
    isDisabled?: boolean
    parentBackgroundColor: string
    lightOutline?: boolean
  }) => {
    const {
      field: { onChange, value },
    } = useController({ control, name })

    const handleChange = (option: SelectOption) => {
      // Save the value to the form
      onChange(option ? option.value : "")
    }

    const onCreateOption = (label: string) => {
      createOptionMutation.mutate(label, {
        onSuccess: (option: SelectOption) => handleChange(option),
      })
    }

    const selectedOption = options.find((option) => option.value === value) || null

    return (
      <div className={classNames(styles.inputContainer, className && className)}>
        <Createable
          options={options}
          value={selectedOption}
          isClearable={true}
          onChange={handleChange}
          placeholder={""}
          onCreateOption={onCreateOption}
          isMulti={false}
          isLoading={createOptionMutation.isPending}
          isDisabled={isDisabled}
          defaultMenuIsOpen={false}
          openMenuOnFocus={true}
          components={{
            DropdownIndicator: ({ selectProps }) =>
              selectProps.menuIsOpen ? (
                <ChevronUpFill className={createableStyles.dropdownIndicator} />
              ) : (
                <ChevronDownFill className={createableStyles.dropdownIndicator} />
              ),
            ClearIndicator: ({ clearValue }) => (
              <div onClick={clearValue} className={createableStyles.clearIndicatorContainer}>
                <CloseOutline className={createableStyles.clearIndicator} />
              </div>
            ),
          }}
          styles={{
            control: (styles, state) => ({
              ...styles,
              borderRadius: "8px",
              borderColor: state.isFocused
                ? colors.strokeBlueAccent
                : lightOutline
                  ? colors.strokeInputLight
                  : colors.strokeInput,
              backgroundColor: "transparent",
              height: "40px",
              padding: "0 12px",
              boxShadow: state.isFocused
                ? `0px 0px 0px 3px ${colors.buttonBadgeDarkBlueDefault}`
                : "none",
              cursor: "pointer",
              "&:hover": {
                borderColor: colors.strokeBlueAccent,
              },
            }),
            input: (styles) => ({
              ...styles,
              color: colors.textPrimary,
              padding: "0px",
              margin: "0px",
              height: "38px",
              fontSize: "14px",
              cursor: "text",
              boxShadow: "none",
            }),
            indicatorSeparator: (styles) => ({
              ...styles,
              display: "none",
            }),
            valueContainer: (styles) => ({
              ...styles,
              padding: "0px",
            }),
            dropdownIndicator: (styles) => ({
              ...styles,
              padding: "0px",
              cursor: "pointer",
              color: colors.iconTertiary,
            }),
            menu: (styles) => ({
              ...styles,
              backgroundColor: colors.backgroundQuaternary,
              boxShadow: `0px 0px 17px 0px rgba(${colors.backgroundPrimary}, 0.08)`,
              borderRadius: "12px",
              padding: "8px 1px 8px 0px",
              color: colors.textPrimary,
            }),
            menuList: (styles) => ({
              ...styles,
              scrollbarWidth: "thin",
              scrollbarColor: `${colors.strokeScroller} transparent`,
              maxHeight: "210px",
              padding: "0px 8px",
              overflowY: "scroll",
              overflowX: "hidden",
              "::-webkit-scrollbar-thumb": {
                borderRadius: "10px",
                backgroundColor: colors.strokeScroller,
              },
              "::-webkit-scrollbar": {
                width: "6px",
              },
              "::-webkit-scrollbar-track": {
                background: colors.backgroundQuaternary,
                borderRadius: "10px",
              },
            }),
            option: (styles, state) => ({
              ...styles,
              fontSize: "14px",
              padding: "6px 12px",
              margin: "4px 0px",
              borderRadius: "10px",
              cursor: "pointer",
              backgroundColor: state.isSelected
                ? colors.buttonTertiaryActive
                : state.isFocused
                  ? colors.buttonTertiaryHover
                  : "transparent",
              "&:hover": {
                backgroundColor: colors.buttonTertiaryHover,
              },
            }),
            singleValue: (styles) => ({
              ...styles,
              color: colors.textPrimary,
              fontSize: "14px",
              margin: "0px",
            }),
            noOptionsMessage: (styles) => ({
              ...styles,
              color: colors.textTertiary,
              fontSize: "16px",
              margin: "0px",
            }),
          }}
        />
        <label
          className={selectedOption && styles.filled}
          htmlFor={name}
          style={{
            backgroundImage:
              isDisabled && !value
                ? "none"
                : `linear-gradient(to top, ${parentBackgroundColor} 6px, transparent 5px)`,
            backgroundColor: "transparent",
          }}
        >
          {label}
        </label>
      </div>
    )
  },
)
